import {
  ClockEstimator,
  gradeLink,
  type ClockEstimate,
  type QualityGrade,
} from '@cinesync/sync-core';
import {
  PROTOCOL_VERSION,
  encodeMessage,
  parseServerMessage,
  type ClientMessage,
  type MediaFingerprint,
  type ServerMessage,
} from '@cinesync/protocol';

/**
 * The client end of a room.
 *
 * Owns the socket, the clock estimate and the reconnection policy, and
 * nothing else. It does not know what a video is: it emits validated server
 * messages and a link quality reading, and the sync controller decides what
 * those mean for playback.
 */

/** How often a clock probe is sent once connected. */
const PROBE_INTERVAL_MS = 2_000;

/** Probes sent back to back at first, to converge quickly on joining. */
const INITIAL_PROBE_BURST = 6;
const INITIAL_PROBE_SPACING_MS = 200;

/** Reconnection backoff, in milliseconds. Repeats the last value thereafter. */
const BACKOFF_MS = [500, 1_000, 2_000, 4_000, 8_000, 15_000] as const;

export type ConnectionStatus =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'failed';

export interface LinkQuality {
  readonly grade: QualityGrade;
  readonly rttMs: number;
  readonly jitterMs: number;
  readonly clock: ClockEstimate | null;
}

export interface RoomConnectionOptions {
  readonly relayUrl: string;
  readonly roomCode: string;
  readonly participantId: string;
  readonly displayName: string;
  readonly media: MediaFingerprint | null;

  readonly onMessage: (message: ServerMessage) => void;
  readonly onStatus: (status: ConnectionStatus) => void;
  readonly onQuality: (quality: LinkQuality) => void;
}

export class RoomConnection {
  readonly #options: RoomConnectionOptions;
  readonly #clock = new ClockEstimator();

  #socket: WebSocket | null = null;
  #status: ConnectionStatus = 'idle';
  #probeTimer: ReturnType<typeof setInterval> | null = null;
  #reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  #attempt = 0;
  #missedProbes = 0;
  #pendingProbes = new Map<number, number>();
  #closedByUs = false;

  constructor(options: RoomConnectionOptions) {
    this.#options = options;
  }

  get clock(): ClockEstimator {
    return this.#clock;
  }

  get status(): ConnectionStatus {
    return this.#status;
  }

  /** Convert a local timestamp to the relay's frame of reference. */
  toServerTime(localMs: number): number {
    return this.#clock.toServerTime(localMs);
  }

  connect(): void {
    this.#closedByUs = false;
    this.#open();
  }

  /** Send a message, if the socket is up. Silently dropped otherwise. */
  send(message: ClientMessage): void {
    const socket = this.#socket;
    if (socket === null || socket.readyState !== WebSocket.OPEN) return;
    socket.send(encodeMessage(message));
  }

  disconnect(): void {
    this.#closedByUs = true;
    this.#teardown();
    this.#setStatus('idle');
  }

  #open(): void {
    this.#setStatus(this.#attempt === 0 ? 'connecting' : 'reconnecting');

    const url = new URL(this.#options.relayUrl);
    url.searchParams.set('room', this.#options.roomCode);
    url.searchParams.set('id', this.#options.participantId);

    let socket: WebSocket;
    try {
      socket = new WebSocket(url);
    } catch {
      this.#scheduleReconnect();
      return;
    }

    this.#socket = socket;

    socket.addEventListener('open', () => {
      this.#attempt = 0;
      this.#missedProbes = 0;
      // A new socket may reach a different edge location with a different
      // clock, so old samples are worse than no samples.
      this.#clock.reset();
      this.#pendingProbes.clear();

      this.send({
        type: 'hello',
        protocolVersion: PROTOCOL_VERSION,
        displayName: this.#options.displayName,
        media: this.#options.media,
      });

      this.#setStatus('connected');
      this.#startProbing();
    });

    socket.addEventListener('message', (event) => {
      // Every frame is validated before anything else sees it. The relay is
      // trusted no more than any other network peer.
      const parsed = parseServerMessage(typeof event.data === 'string' ? event.data : '');
      if (!parsed.ok) return;

      if (parsed.message.type === 'pong') {
        this.#onPong(parsed.message);
        return;
      }

      this.#options.onMessage(parsed.message);
    });

    const onGone = () => {
      if (this.#closedByUs) return;
      this.#stopProbing();
      this.#scheduleReconnect();
    };

    socket.addEventListener('close', onGone);
    socket.addEventListener('error', onGone);
  }

  #onPong(message: Extract<ServerMessage, { type: 'pong' }>): void {
    const sentAt = this.#pendingProbes.get(message.t0);
    if (sentAt === undefined) return;

    this.#pendingProbes.delete(message.t0);
    this.#missedProbes = 0;

    this.#clock.addSample({
      t0: message.t0,
      t1: message.t1,
      t2: message.t2,
      t3: performance.now(),
    });

    this.#publishQuality();
  }

  #startProbing(): void {
    this.#stopProbing();

    // A burst first, so the estimate converges before playback begins rather
    // than over the following minute.
    for (let i = 0; i < INITIAL_PROBE_BURST; i += 1) {
      setTimeout(() => this.#probe(), i * INITIAL_PROBE_SPACING_MS);
    }

    this.#probeTimer = setInterval(() => this.#probe(), PROBE_INTERVAL_MS);
  }

  #probe(): void {
    const socket = this.#socket;
    if (socket === null || socket.readyState !== WebSocket.OPEN) return;

    // `performance.now()` rather than `Date.now()`: it is monotonic, so a
    // system clock correction mid-session cannot corrupt the estimate.
    const t0 = performance.now();
    this.#pendingProbes.set(t0, t0);

    // Anything still outstanding after a few rounds is counted as lost, which
    // is what drives the link grade down before the socket actually dies.
    for (const [key, sentAt] of this.#pendingProbes) {
      if (t0 - sentAt > PROBE_INTERVAL_MS * 3) {
        this.#pendingProbes.delete(key);
        this.#missedProbes += 1;
      }
    }

    this.send({ type: 'ping', t0 });
    if (this.#missedProbes > 0) this.#publishQuality();
  }

  #stopProbing(): void {
    if (this.#probeTimer !== null) clearInterval(this.#probeTimer);
    this.#probeTimer = null;
  }

  #publishQuality(): void {
    const estimate = this.#clock.estimate;
    const rttMs = estimate?.rttMs ?? 0;
    const jitterMs = estimate?.uncertaintyMs ?? 0;

    this.#options.onQuality({
      grade: gradeLink({ rttMs, jitterMs, missedHeartbeats: this.#missedProbes }),
      rttMs,
      jitterMs,
      clock: estimate,
    });
  }

  #scheduleReconnect(): void {
    this.#teardownSocket();
    this.#setStatus('reconnecting');

    // Backoff, so a relay that is down is not hammered, while a brief blip
    // recovers in half a second.
    const delay = BACKOFF_MS[Math.min(this.#attempt, BACKOFF_MS.length - 1)]!;
    this.#attempt += 1;

    if (this.#reconnectTimer !== null) clearTimeout(this.#reconnectTimer);
    this.#reconnectTimer = setTimeout(() => this.#open(), delay);
  }

  #teardownSocket(): void {
    const socket = this.#socket;
    this.#socket = null;
    if (socket === null) return;

    try {
      socket.close();
    } catch {
      // Already closing. Nothing useful to do.
    }
  }

  #teardown(): void {
    this.#stopProbing();
    if (this.#reconnectTimer !== null) clearTimeout(this.#reconnectTimer);
    this.#reconnectTimer = null;
    this.#teardownSocket();
    this.#clock.reset();
  }

  #setStatus(status: ConnectionStatus): void {
    if (this.#status === status) return;
    this.#status = status;
    this.#options.onStatus(status);
  }
}

/** Backoff schedule, exported so the reconnection UI can describe the wait. */
export { BACKOFF_MS };
